import React, { useState, useMemo } from 'react';
import { CompleteKundali, Language } from '../types/kundali';
import {
  analyzeCareerTimeline,
  CareerAptitudeVerdict,
  ComprehensiveCareerAnalysis,
  DualTrackPhase,
  ExtendedCareerHorizonPhase
} from '../utils/careerTimelineEngine';
import {
  Briefcase,
  TrendingUp,
  Building2,
  Calendar,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Target,
  ArrowRight,
  Shield,
  Layers,
  Award,
  Crown,
  Compass,
  Coins,
  FileText
} from 'lucide-react';

interface CareerBusinessVsJobModuleProps {
  kundali: CompleteKundali;
  lang?: Language;
}

export const CareerBusinessVsJobModule: React.FC<CareerBusinessVsJobModuleProps> = ({
  kundali,
  lang = 'en'
}) => {
  const isHi = lang === 'hi';

  const analysis: ComprehensiveCareerAnalysis = useMemo(() => {
    return analyzeCareerTimeline(kundali, lang);
  }, [kundali, lang]);

  // Collapsible Accordion States (Next 5 Years is permanently open)
  const [isAptitudeOpen, setIsAptitudeOpen] = useState<boolean>(true);
  const [isAstrologicalDriversOpen, setIsAstrologicalDriversOpen] = useState<boolean>(false);
  const [isBeyond5YearsOpen, setIsBeyond5YearsOpen] = useState<boolean>(false);

  const { aptitude, next5YearsPhases, beyond5YearsPhases } = analysis;

  return (
    <div className="space-y-6">
      {/* 1. Header Banner */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 shadow-2xs space-y-2">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-astro-primary text-white flex items-center justify-center shrink-0 shadow-xs">
              <Briefcase className="w-5 h-5 text-astro-accent" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-astro-primary font-serif">
                {isHi ? 'कैरियर विश्लेषण: व्यवसाय बनाम नौकरी एवं सटीक काल निर्धारण' : 'Career Intelligence: Business vs. Job & Predictive Timings'}
              </h2>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'षष्ठम भाव (सेवा) बनाम सप्तम भाव (व्यापार), आगामी ५ वर्षों का समानांतर समय चक्र एवं दीर्घकालिक भविष्य'
                  : '6th House (Service) vs 7th House (Commerce), 5-Year Dual-Track Horizon & Extended Destiny'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-astro-primary/10 text-astro-primary border border-astro-primary/20">
              {isHi ? aptitude.verdictTitleHi : aptitude.verdictTitleEn}
            </span>
          </div>
        </div>
      </div>

      {/* 2. Collapsible Tab 1: Foundational Aptitude (Business vs. Job Verdict) */}
      <div className="bg-astro-card border border-astro-border rounded-2xl shadow-2xs overflow-hidden transition-all">
        <button
          onClick={() => setIsAptitudeOpen(!isAptitudeOpen)}
          className="w-full p-4 sm:p-5 flex items-center justify-between gap-4 text-left hover:bg-astro-bg/60 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2.5">
            <TrendingUp className="w-5 h-5 text-astro-accent shrink-0" />
            <div>
              <h3 className="text-base font-bold text-astro-text font-serif">
                {isHi ? '१. जन्मजात योग्यता एवं प्राथमिकता (व्यवसाय बनाम नौकरी)' : '1. Foundational Aptitude & Core Tendency (Business vs. Job)'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'योग्यता प्रतिशत, प्राथमिक मार्ग एवं शास्त्रीय सारांश' : 'Aptitude split, primary orientation & scriptural synthesis'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-astro-primary font-mono hidden sm:inline-block">
              {aptitude.businessScore}% {isHi ? 'व्यवसाय' : 'Biz'} • {aptitude.jobScore}% {isHi ? 'नौकरी' : 'Job'}
            </span>
            {isAptitudeOpen ? (
              <ChevronUp className="w-5 h-5 text-astro-secondary-text" />
            ) : (
              <ChevronDown className="w-5 h-5 text-astro-secondary-text" />
            )}
          </div>
        </button>

        {isAptitudeOpen && (
          <div className="p-4 sm:p-5 pt-0 border-t border-astro-border/50 space-y-4">
            {/* Visual Ratio Bar */}
            <div className="bg-astro-bg border border-astro-border rounded-xl p-4 space-y-2">
              <div className="flex items-center justify-between text-xs font-bold">
                <span className="text-emerald-700 flex items-center gap-1.5">
                  <TrendingUp className="w-4 h-4" /> {isHi ? 'स्वतंत्र व्यवसाय / उद्यम' : 'Independent Business / Enterprise'} ({aptitude.businessScore}%)
                </span>
                <span className="text-blue-700 flex items-center gap-1.5">
                  <Building2 className="w-4 h-4" /> {isHi ? 'कॉर्पोरेट / संगठनात्मक सेवा' : 'Corporate Service / Job'} ({aptitude.jobScore}%)
                </span>
              </div>

              {/* Progress Bar */}
              <div className="h-3.5 w-full bg-blue-100 rounded-full overflow-hidden flex shadow-inner">
                <div
                  className="h-full bg-emerald-600 transition-all duration-700 rounded-l-full"
                  style={{ width: `${(aptitude.businessScore / (aptitude.businessScore + aptitude.jobScore)) * 100}%` }}
                />
                <div
                  className="h-full bg-blue-600 transition-all duration-700 rounded-r-full"
                  style={{ width: `${(aptitude.jobScore / (aptitude.businessScore + aptitude.jobScore)) * 100}%` }}
                />
              </div>

              <div className="text-[11px] text-astro-secondary-text flex items-center justify-between pt-0.5">
                <span>7th House (Trade) • 3rd House (Risk) • Mercury</span>
                <span>6th House (Service) • 10th House (Status) • Saturn</span>
              </div>
            </div>

            {/* Core Recommendation Callout */}
            <div className="bg-astro-primary/5 border-l-4 border-astro-primary p-4 rounded-r-xl space-y-1">
              <div className="text-xs font-bold text-astro-primary uppercase tracking-wide">
                {isHi ? 'शास्त्रीय निष्कर्ष एवं रणनीति' : 'Astrological Verdict & Core Strategy'}
              </div>
              <p className="text-xs text-astro-text leading-relaxed font-medium">
                {isHi ? aptitude.summaryHi : aptitude.summaryEn}
              </p>
            </div>

            {/* Quick Metrics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div className="bg-astro-bg border border-astro-border p-3 rounded-xl space-y-1">
                <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">
                  {isHi ? 'षष्ठम vs सप्तम भाव संतुलन' : '6th vs 7th House Balance'}
                </span>
                <div className="font-bold text-astro-text text-sm">
                  {aptitude.h6VsH7Comparison.favoredPath === 'BUSINESS'
                    ? (isHi ? 'सप्तम भाव (व्यापार) प्रबल' : '7th House Favored (Trade)')
                    : aptitude.h6VsH7Comparison.favoredPath === 'JOB'
                    ? (isHi ? 'षष्ठम भाव (सेवा) प्रबल' : '6th House Favored (Job)')
                    : (isHi ? 'संतुलित समन्वय' : 'Equally Balanced')}
                </div>
                <div className="text-[11px] text-astro-secondary-text">
                  6th SAV: {aptitude.h6VsH7Comparison.h6SAV} • 7th SAV: {aptitude.h6VsH7Comparison.h7SAV}
                </div>
              </div>

              <div className="bg-astro-bg border border-astro-border p-3 rounded-xl space-y-1">
                <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">
                  {isHi ? 'पराक्रम एवं जोखिम क्षमता' : 'Risk & Parakrama Quotient'}
                </span>
                <div className="font-bold text-astro-text text-sm">
                  {aptitude.parakramaRiskQuotient.riskCategory === 'HIGH_APPETITE'
                    ? (isHi ? 'उच्च जोखिम क्षमता (उद्यम अनुकूल)' : 'High Risk Appetite (Founder)')
                    : aptitude.parakramaRiskQuotient.riskCategory === 'CALCULATED_MODERATE'
                    ? (isHi ? 'मापा-तौला संतुलित जोखिम' : 'Calculated Moderate Risk')
                    : (isHi ? 'सुरक्षित स्थिर वरीयता' : 'Risk Averse (Stability First)')}
                </div>
                <div className="text-[11px] text-astro-secondary-text">
                  3rd House SAV: {aptitude.parakramaRiskQuotient.h3SAV} Bindus
                </div>
              </div>

              <div className="bg-astro-bg border border-astro-border p-3 rounded-xl space-y-1">
                <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">
                  {isHi ? 'धन एवं लाभ अक्ष (२/११ भाव)' : 'Wealth & Revenue Axis (2nd & 11th)'}
                </span>
                <div className="font-bold text-astro-text text-sm truncate">
                  11th Labha: {aptitude.wealthAccumulationAxis.h11SAV} SAV
                </div>
                <div className="text-[11px] text-astro-secondary-text">
                  2nd Dhana: {aptitude.wealthAccumulationAxis.h2SAV} SAV
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. Collapsible Tab 2: Key Astrological Factors & Drivers */}
      <div className="bg-astro-card border border-astro-border rounded-2xl shadow-2xs overflow-hidden transition-all">
        <button
          onClick={() => setIsAstrologicalDriversOpen(!isAstrologicalDriversOpen)}
          className="w-full p-4 sm:p-5 flex items-center justify-between gap-4 text-left hover:bg-astro-bg/60 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2.5">
            <Layers className="w-5 h-5 text-astro-accent shrink-0" />
            <div>
              <h3 className="text-base font-bold text-astro-text font-serif">
                {isHi ? '२. शास्त्रीय कारक एवं दोनों मार्गों के विशिष्ट सामर्थ्य' : '2. Astrological Drivers & Specific Advantages for Job and Business'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'नौकरी में लाभप्रद बिंदु बनाम व्यवसाय में सफलता के कारक' : 'Comparative breakdown of strengths in corporate tenure vs enterprise'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAstrologicalDriversOpen ? (
              <ChevronUp className="w-5 h-5 text-astro-secondary-text" />
            ) : (
              <ChevronDown className="w-5 h-5 text-astro-secondary-text" />
            )}
          </div>
        </button>

        {isAstrologicalDriversOpen && (
          <div className="p-4 sm:p-5 pt-0 border-t border-astro-border/50 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Job Strengths */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-blue-700 font-bold text-xs uppercase tracking-wide border-b border-astro-border/50 pb-2">
                  <Building2 className="w-4 h-4" /> {isHi ? 'नौकरी / सेवा में स्वाभाविक सामर्थ्य' : 'Core Strengths in Corporate Employment'}
                </div>
                <ul className="space-y-2 text-xs">
                  {aptitude.strengthsJob.map((s, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-astro-text">
                      <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-0.5" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Business Strengths */}
              <div className="bg-astro-bg border border-astro-border rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-emerald-700 font-bold text-xs uppercase tracking-wide border-b border-astro-border/50 pb-2">
                  <TrendingUp className="w-4 h-4" /> {isHi ? 'व्यवसाय / उद्यम में स्वाभाविक सामर्थ्य' : 'Core Strengths in Business & Enterprise'}
                </div>
                <ul className="space-y-2 text-xs">
                  {aptitude.strengthsBusiness.map((s, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-astro-text">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 4. PERMANENTLY OPEN TAB: NEXT 5 YEARS CAREER CLIMATE (2026 - 2031) */}
      <div className="bg-astro-card border-2 border-astro-primary/40 rounded-2xl p-5 shadow-sm space-y-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-astro-border pb-3">
          <div className="flex items-center gap-2.5">
            <span className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
            <div>
              <h3 className="text-lg font-bold text-astro-primary font-serif flex items-center gap-2">
                <span>{isHi ? '★ आगामी ५ वर्षों का समानांतर समय चक्र (Next 5 Years Timeline)' : '★ Next 5 Years Dual-Track Career Timeline'}</span>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300">
                  {isHi ? 'सक्रिय मार्गदर्शन' : 'Permanently Active'}
                </span>
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'नौकरी में पदोन्नति/परिवर्तन एवं व्यवसाय शुभारंभ के लिए चरण-दर-चरण सटीक मुहूर्त एवं परामर्श'
                  : 'Phase-by-phase opportunities for Job promotion/switch AND Business launch evaluated simultaneously'}
              </p>
            </div>
          </div>

          <div className="text-right text-xs text-astro-secondary-text">
            <span>{next5YearsPhases.length} {isHi ? 'विस्तृत चरण' : 'Distinct Chronological Phases'}</span>
          </div>
        </div>

        {/* Phase-by-Phase Cards */}
        <div className="space-y-4">
          {next5YearsPhases.map((phase, idx) => (
            <div
              key={phase.id}
              className="bg-astro-bg border border-astro-border rounded-2xl p-5 space-y-4 shadow-2xs hover:border-astro-primary/40 transition-all"
            >
              {/* Phase Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-astro-border/60 pb-3">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-xl bg-astro-primary text-white text-xs font-bold flex items-center justify-center font-mono">
                    0{idx + 1}
                  </span>
                  <div>
                    <h4 className="font-bold text-astro-text text-sm font-serif">
                      {phase.timeRange} • {phase.activeDasha}
                    </h4>
                    <div className="text-[11px] text-astro-secondary-text">
                      {isHi ? 'शास्त्रीय प्रभाव: ' : 'Planetary Drivers: '}{phase.planetaryClimate.join(' • ')}
                    </div>
                  </div>
                </div>

                <div className="px-3 py-1 rounded-xl bg-astro-card border border-astro-border text-xs font-bold text-astro-primary">
                  {phase.activeDasha}
                </div>
              </div>

              {/* Dual-Track Side-by-Side Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                
                {/* 1. Job / Employment Track Card */}
                <div className="bg-astro-card border border-blue-500/20 rounded-xl p-4 space-y-3 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2 border-b border-astro-border/50 pb-2">
                      <div className="flex items-center gap-2 text-blue-700 font-bold text-xs uppercase tracking-wide">
                        <Building2 className="w-4 h-4" />
                        <span>{isHi ? 'नौकरी / सेवा का मार्ग' : 'Job / Employment Track'}</span>
                      </div>
                      <span className="font-mono text-xs font-bold text-blue-700 px-2 py-0.5 rounded bg-blue-50 border border-blue-200">
                        {phase.jobTrack.score}/100
                      </span>
                    </div>

                    <div>
                      <div className="font-bold text-astro-text text-sm">
                        {isHi ? phase.jobTrack.statusLabelHi : phase.jobTrack.statusLabelEn}
                      </div>
                      <div className="text-[11px] text-blue-700 font-semibold mt-0.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{isHi ? 'अनुकूल कालखंड: ' : 'Optimal Window: '} {isHi ? phase.jobTrack.keyWindowHi : phase.jobTrack.keyWindowEn}</span>
                      </div>
                    </div>

                    <p className="text-xs text-astro-secondary-text leading-relaxed">
                      {isHi ? phase.jobTrack.adviceHi : phase.jobTrack.adviceEn}
                    </p>
                  </div>

                  {/* Recommended Actions */}
                  <div className="pt-2 border-t border-astro-border/50 space-y-1">
                    <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">
                      {isHi ? 'अनुशंसित कदम:' : 'Strategic Actions:'}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {phase.jobTrack.favorableActions.map((act, aIdx) => (
                        <span key={aIdx} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">
                          ✓ {act}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 2. Business / Venture Track Card */}
                <div className="bg-astro-card border border-emerald-500/20 rounded-xl p-4 space-y-3 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2 border-b border-astro-border/50 pb-2">
                      <div className="flex items-center gap-2 text-emerald-700 font-bold text-xs uppercase tracking-wide">
                        <TrendingUp className="w-4 h-4" />
                        <span>{isHi ? 'व्यवसाय / उद्यम का मार्ग' : 'Business / Venture Track'}</span>
                      </div>
                      <span className="font-mono text-xs font-bold text-emerald-700 px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200">
                        {phase.businessTrack.score}/100
                      </span>
                    </div>

                    <div>
                      <div className="font-bold text-astro-text text-sm">
                        {isHi ? phase.businessTrack.statusLabelHi : phase.businessTrack.statusLabelEn}
                      </div>
                      <div className="text-[11px] text-emerald-700 font-semibold mt-0.5 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{isHi ? 'अनुकूल कालखंड: ' : 'Launch / Action Window: '} {isHi ? phase.businessTrack.keyWindowHi : phase.businessTrack.keyWindowEn}</span>
                      </div>
                    </div>

                    <p className="text-xs text-astro-secondary-text leading-relaxed">
                      {isHi ? phase.businessTrack.adviceHi : phase.businessTrack.adviceEn}
                    </p>
                  </div>

                  {/* Recommended Sectors */}
                  <div className="pt-2 border-t border-astro-border/50 space-y-1">
                    <span className="text-[10px] text-astro-secondary-text uppercase font-bold block">
                      {isHi ? 'अनुकूल क्षेत्र:' : 'Favorable Sectors:'}
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {phase.businessTrack.recommendedSectors.map((sec, sIdx) => (
                        <span key={sIdx} className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                          ✨ {sec}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

              </div>

              {/* Strategic Crossroads Synthesis */}
              <div className="bg-astro-primary/5 border border-astro-primary/20 rounded-xl p-3 flex items-start gap-2 text-xs">
                <Zap className="w-4 h-4 text-astro-primary shrink-0 mt-0.5" />
                <div>
                  <strong className="text-astro-primary mr-1">
                    {isHi ? 'निर्णय मार्गदर्शन:' : 'Pivot Verdict:'}
                  </strong>
                  <span className="text-astro-text">
                    {isHi ? phase.strategicSynthesisHi : phase.strategicSynthesisEn}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 5. ON-DEMAND ACCORDION: BEYOND 5 YEARS HORIZON (2031 - 2041+) */}
      <div className="bg-astro-card border border-astro-border rounded-2xl shadow-2xs overflow-hidden transition-all">
        <button
          onClick={() => setIsBeyond5YearsOpen(!isBeyond5YearsOpen)}
          className="w-full p-4 sm:p-5 flex items-center justify-between gap-4 text-left hover:bg-astro-bg/60 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2.5">
            <Compass className="w-5 h-5 text-astro-accent shrink-0" />
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-astro-text font-serif">
                  {isHi ? '५ वर्षों से आगे का भविष्य (Beyond 5 Years • 2031 - 2041+)' : 'Extended Destiny: Beyond 5 Years Horizon (2031 - 2041+)'}
                </h3>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-800 border border-purple-200">
                  {isHi ? 'अनुरोध पर उपलब्ध' : 'On-Demand'}
                </span>
              </div>
              <p className="text-xs text-astro-secondary-text">
                {isHi
                  ? 'दीर्घकालिक पद प्रतिष्ठा, उद्योग निर्माण, इक्विटी वसीयत एवं महादशा परिवर्तन'
                  : 'Long-term corporate board directorship vs enterprise empire building & equity liquidation'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-astro-primary hidden sm:inline-block">
              {isBeyond5YearsOpen ? (isHi ? 'संक्षिप्त करें' : 'Hide Extended') : (isHi ? 'विस्तार से देखें' : 'Reveal Beyond 5 Years')}
            </span>
            {isBeyond5YearsOpen ? (
              <ChevronUp className="w-5 h-5 text-astro-secondary-text" />
            ) : (
              <ChevronDown className="w-5 h-5 text-astro-secondary-text" />
            )}
          </div>
        </button>

        {isBeyond5YearsOpen && (
          <div className="p-4 sm:p-5 pt-0 border-t border-astro-border/50 space-y-4">
            <p className="text-xs text-astro-secondary-text leading-relaxed">
              {isHi
                ? '५ वर्षों से अधिक का कालखंड जीवन की प्रमुख महादशाओं और दीर्घकालिक कर्म योगों पर निर्भर करता है। नीचे दोनों मार्गों (नौकरी के शीर्ष पद बनाम स्थापित उद्यम) का १०-वर्षीय भविष्य प्रदर्शित है:'
                : 'Beyond the 5-year tactical window, career evolution is shaped by macro Mahadasha shifts and the compounding of 2nd & 11th house Dhana Yogas. Below is the 10-year outlook for both executive corporate stature and enterprise ownership:'}
            </p>

            <div className="space-y-4">
              {beyond5YearsPhases.map((bPhase) => (
                <div
                  key={bPhase.id}
                  className="bg-astro-bg border border-astro-border rounded-xl p-4 space-y-3"
                >
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-astro-border/50 pb-2">
                    <span className="font-bold text-sm text-astro-primary font-serif">
                      {bPhase.periodLabel}
                    </span>
                    <span className="text-xs font-mono text-astro-secondary-text">
                      {bPhase.approximateYears}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    {/* Job Long Term */}
                    <div className="bg-astro-card border border-blue-500/20 p-3.5 rounded-xl space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-blue-700 flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5" />
                          {isHi ? 'नौकरी में संभावित पद प्रतिष्ठा' : 'Corporate Apex Stature'}
                        </span>
                        <span className="font-mono font-bold text-blue-700">{bPhase.jobTrajectory.score}/100</span>
                      </div>
                      <div className="font-bold text-astro-text text-sm">
                        {isHi ? bPhase.jobTrajectory.statureHi : bPhase.jobTrajectory.statureEn}
                      </div>
                      <p className="text-astro-secondary-text leading-relaxed">
                        {isHi ? bPhase.jobTrajectory.outlookHi : bPhase.jobTrajectory.outlookEn}
                      </p>
                    </div>

                    {/* Business Long Term */}
                    <div className="bg-astro-card border border-emerald-500/20 p-3.5 rounded-xl space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-emerald-700 flex items-center gap-1.5">
                          <TrendingUp className="w-3.5 h-3.5" />
                          {isHi ? 'व्यवसाय में संभावित साम्राज्य' : 'Enterprise & Wealth Scale'}
                        </span>
                        <span className="font-mono font-bold text-emerald-700">{bPhase.businessTrajectory.score}/100</span>
                      </div>
                      <div className="font-bold text-astro-text text-sm">
                        {isHi ? bPhase.businessTrajectory.statureHi : bPhase.businessTrajectory.statureEn}
                      </div>
                      <p className="text-astro-secondary-text leading-relaxed">
                        {isHi ? bPhase.businessTrajectory.outlookHi : bPhase.businessTrajectory.outlookEn}
                      </p>
                    </div>
                  </div>

                  {/* Macro Themes */}
                  <div className="pt-1">
                    <span className="text-[10px] text-astro-secondary-text uppercase font-bold block mb-1">
                      {isHi ? 'दीर्घकालिक विषय:' : 'Macro Destiny Themes:'}
                    </span>
                    <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                      {bPhase.macroThemes.map((theme, tIdx) => (
                        <li key={tIdx} className="bg-astro-card border border-astro-border px-2.5 py-1.5 rounded-lg text-astro-text flex items-center gap-1.5">
                          <Crown className="w-3 h-3 text-amber-500 shrink-0" />
                          <span>{theme}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
